<div class="dialog-container">
    <!-- Dialog Header -->
    <div class="dialog-header d-flex justify-content-between align-items-center mb-4">
        <h4 class="dialog-title mb-0">
            <span *ngIf="isHoster">Meeting Summary - {{ meetingTitle }}</span>
            <span *ngIf="!isHoster">Agenda Responses Recap</span>
        </h4>
        <button type="button" class="close-btn" (click)="onClose()" aria-label="Close dialog">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 5L5 15M5 5L15 15" stroke="#333" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
        </button>
    </div>

    <!-- Hoster View: Agenda Navigation and Members' Responses Table -->
    <div *ngIf="isHoster" class="hoster-view">
        <!-- Agenda Items Navigation -->
        <div class="agenda-navigation mb-4" *ngIf="agendaItems.length > 1">
            <div class="agenda-items-list">
                <div 
                    *ngFor="let item of agendaItems; let i = index" 
                    class="agenda-item-tab"
                    [class.active]="i === currentAgendaIndex"
                    (click)="selectAgendaItem(i)">
                    <span class="agenda-tab-number">{{ i + 1 }}</span>
                    <span class="agenda-tab-subject">{{ item.subject }}</span><span class="agenda-stage-pill" [ngClass]="getAgendaStageClass(item.currentStage)" *ngIf="item.currentStage">{{ item.currentStage }}</span>
                </div>
            </div>
        </div>

        <!-- Current Agenda Item Info -->
        <div class="agenda-info mb-3" *ngIf="agendaItems[currentAgendaIndex]">
            <h5 class="agenda-title mb-2">
                <span class="agenda-number">Agenda {{ currentAgendaIndex + 1 }}:</span>
                <span class="agenda-subject">{{ agendaItems[currentAgendaIndex].subject }}</span>
            </h5>
        </div>

        <!-- Response Summary Pills -->
        <div class="response-summary d-flex gap-2 mb-3">
            <span class="response-pill accepted-pill" *ngIf="currentAcceptedCount > 0">
                {{ currentAcceptedCount }} Accepted
            </span>
            <span class="response-pill rejected-pill" *ngIf="currentRejectedCount > 0">
                {{ currentRejectedCount }} Rejected
            </span>
            <span class="response-pill deferred-pill" *ngIf="currentDeferredCount > 0">
                {{ currentDeferredCount }} Deferred
            </span>
        </div>

        <!-- Members' Responses Table -->
        <div class="responses-table-container">
            <table class="responses-table">
                <thead>
                    <tr>
                        <th>User</th>
                        <th>Response</th>
                        <th>Comment</th>
                    </tr>
                </thead>
                <tbody>
                    <tr *ngFor="let answer of currentAgendaAnswers">
                        <td class="user-cell">
                            <div class="d-flex align-items-center gap-2">
                                <div class="user-avatar">
                                    {{ getUserInitials(answer.userName) }}
                                </div>
                                <span class="user-name">{{ answer.userName || 'User' }}</span>
                            </div>
                        </td>
                        <td class="response-cell">
                            <span [class]="getResponseBadgeClass(answer.action)">
                                <i class="fas" [class]="getResponseIcon(answer.action) + ' me-1'"></i>
                                {{ getResponseText(answer.action) }}
                            </span>
                        </td>
                        <td class="comment-cell">
                            <div class="d-flex align-items-center gap-2">
                                <i class="fas fa-comment text-muted"></i>
                                <span class="comment-text">{{ answer.comment || 'No comment provided' }}</span>
                            </div>
                        </td>
                    </tr>
                    <tr *ngIf="currentAgendaAnswers.length === 0">
                        <td colspan="3" class="text-center text-muted p-4">
                            No responses yet for this agenda item.
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>

    <!-- Member View: Their Own Responses -->
    <div *ngIf="!isHoster" class="member-view">
        <!-- Summary Card -->
        <div class="summary-card">
            <!-- Response Summary Pills -->
            <div class="response-summary d-flex gap-2 mb-3">
                <span class="response-pill accepted-pill" *ngIf="getAcceptedCount() > 0">
                    {{ getAcceptedCount() }} Accepted
                </span>
                <span class="response-pill rejected-pill" *ngIf="getRejectedCount() > 0">
                    {{ getRejectedCount() }} Rejected
                </span>
                <span class="response-pill deferred-pill" *ngIf="getDeferredCount() > 0">
                    {{ getDeferredCount() }} Deferred
                </span>
            </div>

            <!-- Responses Table -->
            <div class="responses-table-container">
                <table class="responses-table">
                    <thead>
                        <tr>
                            <th>Agenda Item</th>
                            <th>Response</th>
                            <th>Comment</th>
                            <th>Submitted</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr *ngFor="let item of allResponses">
                            <td class="agenda-item-cell">
                                <div class="agenda-item-title fw-bold">{{ item.subject }}</div>
                                <div class="agenda-datetime text-muted">{{ item.dateTime | date: 'short' }}</div>
                            </td>
                            <td class="response-cell">
                                <div class="response-content d-flex align-items-center gap-2">
                                    <i class="fas fa-{{ item.responseIcon }} text-{{ item.response === 'Accepted' ? 'success' : 'danger' }}"></i>
                                    <span class="response-text">{{ item.response }}</span>
                                </div>
                            </td>
                            <td>
                                <div class="comment-cell d-flex align-items-center gap-2">
                                    <i class="fas fa-comment text-muted"></i>
                                    <span class="comment-text">{{ item.comment || 'No comment' }}</span>
                                </div>
                            </td>
                            <td class="submitted-date-cell">
                                <span class="submitted-date">{{ item.submittedDate || item.dateTime || 'N/A' }}</span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- No items message -->
        <div class="text-center p-4" *ngIf="allResponses.length === 0">
            <p class="text-muted">No agenda items processed.</p>
        </div>
    </div>

    <!-- Dialog Footer -->
    <div class="dialog-footer d-flex justify-content-end mt-4">
        <button type="button" class="btn btn-primary" (click)="onClose()">Close</button>
    </div>
</div>
